import { useEffect, useMemo, useState, useRef, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ApiService } from "../../../../services/api";
import { useAuth } from "../../../../contexts/AuthContext";

interface DepartmentItem {
  id: string;
  name: string;
  parentId?: string | null;
  color?: string;
  hasChildren?: boolean;
}

interface DepartmentTreeSelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  onSelectWithDetails?: (department: { id: string; name: string; color?: string } | null) => void;
  getItemLabel?: (department: { id: string; name: string; color?: string; parentId?: string | null; hasChildren?: boolean }) => ReactNode;
  departmentOptions?: DepartmentItem[];
  className?: string;
  disabled?: boolean;
  lessonId?: string | null;
  multiple?: boolean;
  rootId?: string | null;
  placeholder?: string;
  teacherId?: string | null;
  hideLeaves?: boolean;
  onlyLeaves?: boolean;
  disableRootSelection?: boolean;
}

export default function DepartmentTreeSelect({
  value,
  onChange,
  onSelectWithDetails,
  getItemLabel,
  departmentOptions,
  className = "",
  disabled = false,
  lessonId,
  multiple = true,
  rootId,
  placeholder = "Departman Seçiniz",
  teacherId,
  hideLeaves = false,
  onlyLeaves = false,
  disableRootSelection = false,
}: DepartmentTreeSelectProps) {
  const { user } = useAuth();
  const [all, setAll] = useState<DepartmentItem[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loadedDepartments, setLoadedDepartments] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [expandingDepartment, setExpandingDepartment] = useState<string | null>(null);

  const requestedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);

        if (Array.isArray(departmentOptions)) {
          const departments: DepartmentItem[] = departmentOptions
            .filter((d: any) => d?.id)
            .map((d: any) => ({
              id: String(d.id || d._id || ""),
              name: String(d.name || d.title || ""),
              parentId: d.parentId ? String(d.parentId) : null,
              color: d.color || d.hexColor || undefined,
              hasChildren: d.hasChildren || false,
            }));

          if (mounted) {
            setAll(departments);
            const depIds = new Set(departments.map((d) => d.id));
            const rootIds = new Set(
              departments
                .filter((d) => !d.parentId || !depIds.has(d.parentId))
                .map((d) => d.id)
            );
            setExpanded(rootIds);
            setLoadedDepartments(new Set(departments.map((d) => d.id)));
          }
          return;
        }

        if (teacherId) {
          const teacherDepartments = await ApiService.getTeacherDepartments(teacherId);
          const departments: DepartmentItem[] = (teacherDepartments || []).map((d: any) => ({
            id: String(d.id || d._id || ""),
            name: String(d.name || d.title || ""),
            parentId: d.parentId ? String(d.parentId) : null,
            color: d.color || d.hexColor || undefined,
            hasChildren: d.hasChildren || false,
          }));

          if (mounted) {
            setAll(departments);

            const depIds = new Set(departments.map(d => d.id));
            const rootIds = new Set(departments.filter(d => !d.parentId || !depIds.has(d.parentId)).map(d => d.id));
            setExpanded(rootIds);
            setLoadedDepartments(new Set(departments.map(d => d.id)));
          }
          return;
        }

        if (rootId) {
          const [rootResponse, childrenResponse] = await Promise.all([
            ApiService.getDepartment(rootId),
            ApiService.getAllDepartments(rootId)
          ]);

          const rootData = rootResponse?.data || rootResponse;
          const childrenData = childrenResponse?.data || childrenResponse || [];

          const items: DepartmentItem[] = [];

          if (rootData && !rootData.deletedAt) {
            items.push({
              id: String(rootData.id || rootData._id || ""),
              name: String(rootData.name || rootData.title || ""),
              parentId: undefined,
              color: rootData.color || rootData.hexColor || undefined,
              hasChildren: true
            });

            if (mounted) {
              setExpanded(prev => {
                const next = new Set(prev);
                next.add(String(rootData.id || rootData._id || ""));
                return next;
              });
              setLoadedDepartments(prev => {
                const next = new Set(prev);
                next.add(String(rootData.id || rootData._id || ""));
                return next;
              });
            }
          }

          const parentMap: Record<string, boolean> = {};
          childrenData.forEach((d: any) => {
            const pid = d.parentId ? String(d.parentId) : null;
            if (pid) parentMap[pid] = true;
          });

          childrenData.forEach((d: any) => {
            const childId = String(d.id || d._id || "");
            const pId = d.parentId ? String(d.parentId) : null;

            if (!d.deletedAt && childId !== String(rootId) && pId === String(rootId)) {
              items.push({
                id: childId,
                name: String(d.name || d.title || ""),
                parentId: rootId,
                color: d.color || d.hexColor || undefined,
                hasChildren: d.hasChildren || parentMap[childId] || false,
              });
            }
          });

          if (mounted) {
            setAll(prev => {
              const existingIds = new Set(items.map(d => d.id));
              const prevFiltered = prev.filter(d => !existingIds.has(d.id));
              return [...items, ...prevFiltered];
            });
          }
        } else if (user?.isSuperAdmin || !lessonId) {
          const response = await ApiService.getAllDepartments(null);
          const allDeps = response?.data || response || [];
          const activeDepartments = allDeps.filter((d: any) => !d?.deletedAt);

          const allDepartments: DepartmentItem[] = activeDepartments
            .map((d: any) => ({
              id: String(d?.id || d?._id || ""),
              name: String(d?.name || d?.title || ""),
              parentId: d?.parentId ? String(d.parentId) : null,
              color: d?.color || d?.hexColor || undefined,
              hasChildren: d?.hasChildren || false,
            }))
            .filter((d: DepartmentItem) => d.id);

          if (mounted) {
            setAll(prev => {
              const existingIds = new Set(allDepartments.map(d => d.id));
              const prevFiltered = prev.filter(d => !existingIds.has(d.id));
              return [...allDepartments, ...prevFiltered];
            });
          }
        } else {
          const responsibleData = await ApiService.getLessonResponsible(lessonId, user?.id);
          const allDepartments: DepartmentItem[] = [];

          if (Array.isArray(responsibleData) && responsibleData.length > 0) {
            const userResponsible = responsibleData[0];
            const departments = userResponsible?.departments || [];
            (Array.isArray(departments) ? departments : []).forEach((d: any) => {
              const id = String(d?.id || d?._id || "");
              if (!id) return;

              allDepartments.push({
                id,
                name: String(d?.name || d?.title || ""),
                parentId: d?.parentId ? String(d.parentId) : null,
                color: d?.color || d?.hexColor || undefined,
                hasChildren: d?.hasChildren || false,
              });
            });
          }

          if (mounted) {
            setAll(prev => {
              const existingIds = new Set(allDepartments.map(d => d.id));
              const prevFiltered = prev.filter(d => !existingIds.has(d.id));
              return [...allDepartments, ...prevFiltered];
            });
          }
        }
      } catch (error) {
        console.error("Departmanlar yüklenirken hata:", error);
        if (mounted) setAll([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [departmentOptions, lessonId, user?.isSuperAdmin, user?.id, rootId, teacherId]);

  const roots = useMemo(() => {
    const allIds = new Set(all.map((d) => d.id));
    return all.filter((d) => !d.parentId || !allIds.has(d.parentId)).sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
  }, [all]);

  const childrenMap = useMemo(() => {
    const map: Record<string, DepartmentItem[]> = {};
    all.forEach((d) => {
      const pid = String(d.parentId || "");
      if (!pid) return;
      if (!map[pid]) map[pid] = [];
      map[pid].push(d);
    });
    Object.keys(map).forEach((k) => map[k].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")));
    return map;
  }, [all]);

  const getVisibleNodes = (nodes: DepartmentItem[], level = 0): Array<DepartmentItem & { level: number }> => {
    let result: Array<DepartmentItem & { level: number }> = [];
    nodes.forEach((n) => {
      if (hideLeaves && !n.hasChildren && (childrenMap[n.id] || []).length === 0) {
        return;
      }

      result.push({ ...n, level });
      if (expanded.has(n.id)) {
        const kids = childrenMap[n.id] || [];
        if (kids.length > 0) result = result.concat(getVisibleNodes(kids, level + 1));
      }
    });
    return result;
  };

  const visible = useMemo(() => getVisibleNodes(roots), [roots, expanded, childrenMap, hideLeaves]);

  useEffect(() => {
    if (!hideLeaves) return;

    const nodesToPeek = visible.filter(node =>
      node.hasChildren &&
      !loadedDepartments.has(node.id) &&
      !requestedIdsRef.current.has(node.id)
    );

    if (nodesToPeek.length > 0) {
      nodesToPeek.forEach(node => {
        requestedIdsRef.current.add(node.id);
        loadChildDepartments(node.id);
      });
    }
  }, [visible, hideLeaves, loadedDepartments]);

  const loadChildDepartments = async (parentId: string) => {
    if (teacherId) return;

    try {
      setExpandingDepartment(parentId);
      const response = await ApiService.getAllDepartments(parentId);
      const allDeps = response?.data || response || [];
      const activeDepartments = allDeps.filter((d: any) => !d?.deletedAt);

      setAll((prev) => {
        const existingIds = new Set(prev.map((d) => d.id));
        const newDepartments = activeDepartments
          .filter((d: any) => !existingIds.has(d.id))
          .map((d: any) => ({
            id: d.id,
            name: d.name,
            parentId: d.parentId,
            color: d.color,
            hasChildren: d.hasChildren || false,
          }));
        return [...prev, ...newDepartments];
      });
      setLoadedDepartments((prev) => new Set([...prev, parentId]));
    } catch (error) {
      console.error("Alt departmanlar yüklenirken hata:", error);
    } finally {
      setExpandingDepartment(null);
    }
  };

  const toggleExpanded = async (id: string) => {
    const wasExpanded = expanded.has(id);

    if (wasExpanded) {
      setExpanded(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } else {
      setExpanded(prev => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      if (!loadedDepartments.has(id)) {
        await loadChildDepartments(id);
      }
    }
  };

  const toggleSelect = (id: string) => {
    const selectedDepartment = all.find(d => d.id === id);

    if (multiple) {
      const set = new Set(value || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      onChange(Array.from(set));
    } else {
      if (value && value.length === 1 && value[0] === id) {
        onChange([]);
        onSelectWithDetails?.(null);
      } else {
        onChange([id]);
        if (selectedDepartment) {
          onSelectWithDetails?.({
            id: selectedDepartment.id,
            name: selectedDepartment.name,
            color: selectedDepartment.color,
          });
        }
      }
    }
  };

  const isNodeImplicitlySelected = (nodeId: string): boolean => {
    if ((value || []).includes(nodeId)) return true;

    const parentById = new Map(all.map((department) => [department.id, department.parentId || null]));
    let currentParentId = parentById.get(nodeId) || null;
    const visited = new Set<string>();

    while (currentParentId && !visited.has(currentParentId)) {
      if ((value || []).includes(currentParentId)) return true;
      visited.add(currentParentId);
      currentParentId = parentById.get(currentParentId) || null;
    }

    return false;
  };

  return (
    <div className={className}>
      <div className="border border-gray-300 rounded-lg overflow-hidden">
        <div className="max-h-64 overflow-auto divide-y divide-gray-200">
          <button
            type="button"
            onClick={() => onChange([])}
            className={`w-full text-left px-3 py-2 text-sm transition-colors ${value && value.length > 0 ? "hover:bg-gray-50 text-gray-800" : "bg-blue-50 text-blue-900"
              }`}
            disabled={Boolean(disabled || loading)}>
            {placeholder}
          </button>

          {loading && (
            <div className="px-3 py-2 text-sm text-gray-500">Yükleniyor...</div>
          )}

          {!loading && visible.map((node) => {
            const isSelected = (value || []).includes(node.id);
            const isImplicitlySelected = isNodeImplicitlySelected(node.id);
            const isAncestorSelected = isImplicitlySelected && !isSelected;
            const directChildren = childrenMap[node.id] || [];
            let showChevron = false;
            if (hideLeaves) showChevron = directChildren.some(child => child.hasChildren || (childrenMap[child.id] || []).length > 0) || (directChildren.length === 0 && (node.hasChildren || false));
            else showChevron = directChildren.length > 0 || (node.hasChildren || false);
            const isRootNode = node.level === 0;
            const rootSelectionBlocked = disableRootSelection && isRootNode && showChevron;

            return (
              <div key={node.id} className="flex items-center" style={{ paddingLeft: `${node.level * 12 + 8}px` }}>
                {showChevron ? (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(node.id)}
                    className="w-8 h-8 -ml-2 flex items-center justify-center text-gray-600 hover:text-gray-800 rounded hover:bg-gray-100"
                    disabled={Boolean(disabled || loading)}>
                    {expanded.has(node.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                ) : (
                  <span className="w-8 h-8" />
                )}
                <button
                  type="button"
                  onClick={() => {
                    if ((onlyLeaves && showChevron) || rootSelectionBlocked) toggleExpanded(node.id);
                    else toggleSelect(node.id);
                  }}
                  className={`flex-1 text-left px-3 py-2 text-sm transition-colors ${isImplicitlySelected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                    } ${(onlyLeaves && showChevron) || rootSelectionBlocked ? "cursor-default opacity-60 font-medium" : ""}`}
                  disabled={Boolean(disabled || loading)}>
                  {getItemLabel ? getItemLabel(node) : node.name}
                  {isAncestorSelected && (
                    <span className="ml-2 text-[10px] text-blue-700 font-normal">(ust departman secili)</span>
                  )}
                  {onlyLeaves && showChevron && (
                    <span className="ml-2 text-[10px] text-gray-400 font-normal">(Sadece Şubeler Seçilebilir)</span>
                  )}
                  {rootSelectionBlocked && (
                    <span className="ml-2 text-[10px] text-gray-400 font-normal">(Alt Departmani Varsa Secilemez)</span>
                  )}
                </button>
              </div>
            );
          })}
          {!loading && visible.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">{teacherId ? "Bu öğretmene tanımlı sınıf yok." : "Departman yok."}</div>}
        </div>
      </div>
    </div>
  );
}